import { gql } from '@apollo/client'

// `filter_panel_sections` is a scalar `Json!` column (NOT NULL, default `[]`)
// — it always comes back as an array, never `null`, so no null-guard is
// needed on read. Shape: `[{ type: 'attribute_group', id: number }, ...]`;
// unrecognised `type`s are skipped by the backend reader, so a config
// written by a newer admin UI degrades gracefully on an older one.
export const GET_FILTER_SETTINGS = gql`
  query GET_FILTER_SETTINGS {
    findFirstShopSetting {
      id
      filter_panel_sections
    }
  }
`

// Uses the same hand-written `updateShopSettingsV2` mutation as
// `ProductSettings`/`LanguageSettings`. Two things the resolver requires:
// - `shopSettingsId` is typed as nullable but is effectively required: the
//   resolver only applies the update when it's truthy, so omitting it is a
//   silent no-op. Always pass the loaded `findFirstShopSetting.id`.
// - `filter_panel_sections` must always be sent (full-replace semantics,
//   `[]` clears it back to "show all groups") — omitting the variable
//   leaves the column untouched, so never drop the key even when the
//   selection is empty.
// The backend validates strictly and throws on: an unsupported `type`, a
// missing/non-integer/non-positive `id`, duplicate ids, or ids that don't
// exist — the admin UI must not be able to produce any of those, and any
// thrown error must be surfaced to the admin, not swallowed.
export const UPDATE_FILTER_SETTINGS = gql`
  mutation UPDATE_FILTER_SETTINGS(
    $shopSettingsId: Int
    $filter_panel_sections: [FilterPanelSectionInput!]
  ) {
    updateShopSettingsV2(
      shopSettingsId: $shopSettingsId
      filter_panel_sections: $filter_panel_sections
    ) {
      id
      filter_panel_sections
    }
  }
`

// Dedicated picker query — deliberately NOT `GET_ATTRIBUTE_GROUPS_FOR_SELECT`
// (`ProductAttributes/productAttributeQueries.ts`), which only selects `id`
// plus profile names. The dev/prod data holds two full sets of the same
// group names from an earlier PIM id-space (one dead, one live), so without
// `code` and a usage signal the two are visually indistinguishable — picking
// the dead twin quietly configures a section the backend auto-hides, with no
// error anywhere. `product_attributes { product_containers { id } }` mirrors
// the tree query's shape (`ProductAttributes/productAttributeQueries.ts`
// `GET_ATTRIBUTES_TREE`) and lets the picker compute a per-group distinct
// product count to flag/sort-last groups with zero usage.
export const GET_ATTRIBUTE_GROUPS_FOR_FILTER_PICKER = gql`
  query GET_ATTRIBUTE_GROUPS_FOR_FILTER_PICKER {
    findManyProductAttributeGroup(orderBy: { id: asc }) {
      id
      code
      product_attribute_group_profiles {
        name
        language
      }
      product_attributes {
        id
        product_containers {
          id
        }
      }
    }
  }
`
